import { fetchPunterPreferences, savePunterPreferences } from '@app/api/proxy/punter-preferences';
import { resolveTenantContext } from '@app/runtime/tenant';

import { resolvePunterContext } from '../application/context';

import type { PunterPreferencesPort } from '../application/ports';
import type { PunterPreferences } from '../domain/types';

const STORAGE_KEY = 'punterPreferences';

function readLocal(): PunterPreferences {
  try {
    const raw = globalThis.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return parsed as PunterPreferences;
  } catch {
    return {};
  }
}

function writeLocal(preferences: PunterPreferences): void {
  try {
    globalThis.localStorage?.setItem(STORAGE_KEY, JSON.stringify(preferences));
  } catch {
    // Ignore quota / private-mode failures; in-memory settings still apply.
  }
}

function session(): { tenantUuid: string; punterId: string; token: string } {
  let tenantUuid = '';
  let punterId = '';
  let token = '';
  try {
    tenantUuid = resolveTenantContext().queries.getTenantId();
    const punter = resolvePunterContext().queries;
    punterId = punter.getId();
    token = punter.getToken();
  } catch {
    tenantUuid = typeof window !== 'undefined' ? String(window.tenantUuid ?? '') : '';
    token = typeof window !== 'undefined' ? (window.punterToken ?? '') : '';
  }
  return { tenantUuid, punterId, token };
}

export function createPunterPreferences(): PunterPreferencesPort {
  return {
    load: async () => {
      const local = readLocal();
      const { tenantUuid, punterId, token } = session();
      if (!token) return local;

      const data = await fetchPunterPreferences({ tenantUuid, punterId, token });
      if (!data || typeof data !== 'object' || Array.isArray(data)) return local;
      return data;
    },

    save: async (preferences) => {
      const { tenantUuid, punterId, token } = session();
      if (!token) {
        writeLocal(preferences);
        return true;
      }
      return savePunterPreferences({ tenantUuid, punterId, token, preferences });
    },
  };
}
